import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { getNarrativeIcon, toast } from '@utilities';
import { createNarrative, readNarrativeType } from '@api/narratives';

import {
  getPromptPillTooltip,
  getPromptPillValue,
  transformPromptSelections,
} from '../../utilities/helpers';

import PreBuildModal from '../../../../components/PreBuildModal';
import { filterPrompts, transformPromptAnswers } from './utilities/helpers';

const ModalBuildNarrative = ({ flowId, route, setIsModalOpen }) => {
  const [activePrompt, setActivePrompt] = useState(null);
  const [buildError, setBuildError] = useState(null);
  const [narrativeTitle, setNarrativeTitle] = useState('');
  const [isBuilding, setIsBuilding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalPrompts, setModalPrompts] = useState([]);
  const [nonPromptAnswers, setNonPromptAnswers] = useState({});
  const [searchPrompts, setSearchPrompts] = useState([]);
  const [sameLevelPrompts, setSameLevelPrompts] = useState([]);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [transformedAnswersMap, setTransformedAnswersMap] = useState({});

  const navigate = useNavigate();
  const abortControllerRef = useRef(null);

  const processAnswers = ({ answers, defaultAnswers, modalPrompts }) => {
    let repromptAnswers;
    if (answers) {
      const { subscribedCategories, subscribedChannels, ...rest } = answers;
      repromptAnswers = {
        subscribed_categories: subscribedCategories,
        subscribed_channel: subscribedChannels,
        ...rest,
      };
    }
    // Use answers if available, otherwise fallback to defaultAnswers
    const answersToProcess = repromptAnswers || defaultAnswers;

    // Transform date_range if needed
    if (answersToProcess?.date_range && !answersToProcess.date_range.date_range) {
      answersToProcess.date_range = { date_range: answersToProcess.date_range };
    }

    const newTransformedAnswersMap = {};
    const defaultNonPromptAnswers = {};

    Object.keys(answersToProcess).forEach((promptKey) => {
      const answers = answersToProcess[promptKey];
      const prompt = modalPrompts.find((p) => p.promptKey === promptKey);

      if (prompt) {
        const transformed = transformPromptSelections({ answers, prompt });
        newTransformedAnswersMap[promptKey] = transformed;
      } else {
        defaultNonPromptAnswers[promptKey] = answersToProcess[promptKey];
      }
    });

    return {
      nonPromptAnswers: defaultNonPromptAnswers,
      selectedAnswers: answersToProcess,
      transformedAnswersMap: newTransformedAnswersMap,
    };
  };

  useEffect(() => {
    const fetchNarrative = async () => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      try {
        const data = await readNarrativeType({
          flowId,
          narrativeId: route?.id,
        });

        const {
          answers = {},
          defaultAnswers = {},
          hierarchySearchPrompts = data?.searchPrompts || [],
          modalPrompts = filterPrompts(data.prompts),
          sameLevelPrompts = [],
        } = data;

        const { nonPromptAnswers, selectedAnswers, transformedAnswersMap } = processAnswers({
          answers,
          defaultAnswers,
          modalPrompts,
        });

        setNarrativeTitle(data.name);
        setTransformedAnswersMap(transformedAnswersMap);
        setNonPromptAnswers(nonPromptAnswers);
        setSelectedAnswers(selectedAnswers);
        setSearchPrompts(hierarchySearchPrompts);
        setSameLevelPrompts(sameLevelPrompts);
        setModalPrompts(modalPrompts);
        setActivePrompt(modalPrompts[0] || null);
      } catch (error) {
        console.error('Error fetching Narrative prompts:', error);
        toast('Failed to load prompts.', { status: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchNarrative();

    return () => abortControllerRef.current?.abort();
  }, []);

  const handleBuildNarrative = useCallback(async () => {
    setBuildError(null);
    setIsBuilding(true);
    try {
      let promptAnswers = transformPromptAnswers(Object.values(transformedAnswersMap).flat());
      promptAnswers = { ...promptAnswers, ...nonPromptAnswers };

      const narrative = await createNarrative({
        id: route?.id,
        promptAnswers,
        title: narrativeTitle,
      });

      if (!narrative?.flowId) {
        setBuildError('There was an error building your Narrative.');
        setIsBuilding(false);
        return;
      }
      navigate(`/dashboard/narrative/${narrative.flowId}`);
      setIsBuilding(false);
      setIsModalOpen(false);
    } catch (error) {
      const promptErrors = error?.response?.data?.['Prompt Errors'] ?? [];
      const errorMessage = promptErrors[0] || 'There was an error building your Narrative';
      setBuildError(errorMessage);

      console.error(errorMessage, error);
      setIsBuilding(false);
    }
  }, [narrativeTitle, setIsModalOpen, transformedAnswersMap]);

  const dateRangeValue = transformedAnswersMap?.date_range?.[0]?.values?.date_range;

  const isBuildDisabled = useMemo(() => {
    if (modalPrompts.length === 0) return true;
    const hasCategory = !!transformedAnswersMap?.categories?.length;
    const hasDateRange = Array.isArray(dateRangeValue) && dateRangeValue.length === 2;
    const hasProduct = !!transformedAnswersMap?.product?.length;
    const hasStore = !!transformedAnswersMap?.place?.length;

    const requiredPrompts = modalPrompts
      .filter((prompt) => prompt.isRequired)
      .map((filteredPrompt) => filteredPrompt.promptKey);

    const promptSatisfied = {
      categories: hasCategory,
      date_range: hasDateRange,
      place: hasStore,
      product: hasProduct,
    };

    const allRequiredFilled = requiredPrompts.every((key) => promptSatisfied[key]);
    return !allRequiredFilled;
  }, [dateRangeValue, modalPrompts, transformedAnswersMap]);

  return (
    <PreBuildModal
      activePrompt={activePrompt}
      buildError={buildError}
      getPillTooltip={getPromptPillTooltip}
      getPillValue={getPromptPillValue}
      handleBuild={handleBuildNarrative}
      headerIcon={getNarrativeIcon(route?.path)}
      isBuildDisabled={isBuildDisabled}
      isBuilding={isBuilding}
      isLoading={isLoading}
      isModalOpen
      name={narrativeTitle}
      prompts={modalPrompts}
      routeId={route?.id}
      routeLabel={`${route?.name} Narrative`}
      sameLevelPrompts={sameLevelPrompts}
      searchPrompts={searchPrompts}
      selectedAnswers={selectedAnswers}
      setActivePrompt={setActivePrompt}
      setIsModalOpen={setIsModalOpen}
      setName={setNarrativeTitle}
      setSelectedAnswers={setSelectedAnswers}
      setTransformedAnswersMap={setTransformedAnswersMap}
      transformedAnswersMap={transformedAnswersMap}
      transformPromptSelections={transformPromptSelections}
      type="Narrative"
    />
  );
};

ModalBuildNarrative.propTypes = {
  flowId: PropTypes.number,
  route: PropTypes.object.isRequired,
  setIsModalOpen: PropTypes.func.isRequired,
};

export default ModalBuildNarrative;
